/**
 * 页面加载耗时采集（供 PAGE_DATA_LOADED、H5/阅读器性能埋点使用）
 *
 * @description
 * - static_load_duration：导航起点 → DOMContentLoaded（DCL）
 * - load_duration：导航起点 → 调用时刻（由 getPageLoadElapsedMs 提供）
 *
 * 兼容 iOS 12+ / Android 7+：Level 2 → performance.timing → readyState / DCL 监听 → performance.now()
 */

/** 当前 HTML 文档从导航开始到 DCL 完成的耗时（ms）；未采集到前为 null */
// 模块级缓存：同一文档生命周期只算一次，避免重复读 Performance / 重复挂监听
let cachedDocumentDclDurationMs: number | null = null;
// DOMContentLoaded 监听只允许挂一次（该模块可能被多处 import）
let documentDclListenerAttached = false;

/**
 * 从 Performance API 解析 DCL 耗时（ms）
 * @returns 成功则返回非负整数；SSR / API 不可用 / 尚未 DCL 则 null
 */
function resolveDocumentDclDurationFromPerformance(): number | null {
    if (typeof window === 'undefined' || typeof performance === 'undefined') {
        return null;
    }

    try {
        // Level 2：Navigation Timing（较新 Safari / Chrome WebView）
        if (typeof performance.getEntriesByType === 'function') {
            const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
            if (nav && nav.domContentLoadedEventEnd > 0) {
                // domContentLoadedEventEnd 相对导航起点的耗时
                return Math.round(nav.domContentLoadedEventEnd);
            }
        }

        // Level 1：performance.timing（iOS 12–14、部分 Android 7 WebView 等 Level 2 不可用）
        const { timing } = performance;
        if (timing && timing.navigationStart > 0 && timing.domContentLoadedEventEnd > timing.navigationStart) {
            return timing.domContentLoadedEventEnd - timing.navigationStart;
        }
    } catch {
        return null;
    }

    return null;
}

/**
 * 尽早写入 cachedDocumentDclDurationMs：能同步读到则缓存，否则挂 DCL 或近似兜底
 */
function ensureDocumentDclDurationCaptured(): void {
    if (cachedDocumentDclDurationMs !== null || typeof window === 'undefined') {
        return;
    }

    const fromPerformance = resolveDocumentDclDurationFromPerformance();
    if (fromPerformance !== null) {
        cachedDocumentDclDurationMs = fromPerformance;
        return;
    }

    // 兜底：已非 loading 时认为 DCL 已发生；now() 为「导航起点 → 当前」近似值（可能略大于真实 DCL）
    if (document.readyState !== 'loading') {
        cachedDocumentDclDurationMs = Math.round(performance.now());
        return;
    }

    if (documentDclListenerAttached) {
        return;
    }
    documentDclListenerAttached = true;
    document.addEventListener(
        'DOMContentLoaded',
        () => {
            // DCL 后优先再读 Timing；仍不可用则用触发时刻的 now()
            cachedDocumentDclDurationMs = resolveDocumentDclDurationFromPerformance() ?? Math.round(performance.now());
        },
        { once: true }
    );
}

// 客户端 bundle 加载时即开始采集，减轻「首包 API 早于本模块 import」导致 DCL 仍为 null 的概率
if (typeof window !== 'undefined') {
    ensureDocumentDclDurationCaptured();
}

/**
 * 获取文档 DCL 耗时（ms，导航起点 → DCL 结束）
 * @returns 已缓存则返回毫秒数；DCL 未发生且无法近似时返回 null（上报侧可转为 -1）
 */
export function getDocumentDclDurationMs(): number | null {
    return cachedDocumentDclDurationMs;
}

/**
 * 获取导航起点至当前经历的毫秒数
 */

export function getNavElapsedMs(): number {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
        return Math.round(performance.now());
    }
    return Date.now();
}

/**
 * 获取导航起点至当前的耗时（ms）
 * @returns performance.now() 取整；无 Performance 时 null。用于 load_duration（首次有效请求上报时刻）
 */
export function getPageLoadElapsedMs(): number | null {
    if (typeof performance === 'undefined' || typeof performance.now !== 'function') {
        return null;
    }
    return Math.round(performance.now());
}
